import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Gift, Plus, Trash2, CheckCircle2, Undo2, Instagram } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { formatBRL, instaHandle, instaUrl } from "@/lib/site";
import RewardProgramForm from "./RewardProgramForm";

const inputCls = "w-full px-3 py-2 rounded-xl border border-border bg-background outline-none focus:border-primary text-sm";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "");
const FILTERS = [["all", "Todas"], ["pending", "Pendentes"], ["paid", "Pagas"]];

const Stat = ({ label, value, sub, testId }) => (
  <div className="bg-white rounded-2xl border border-border p-4" data-testid={testId}>
    <p className="text-xs font-semibold text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold text-slate-900 font-heading">{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const NewRewardDialog = ({ open, onOpenChange, defaultAmount, onSaved }) => {
  const [form, setForm] = useState({ participant_name: "", instagram: "", amount: defaultAmount });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ participant_name: "", instagram: "", amount: defaultAmount });
  }, [open, defaultAmount]);

  const save = async () => {
    if (!(Number(form.amount) > 0)) { toast.error("Informe o valor da recompensa"); return; }
    setSaving(true);
    try {
      await api.post("/admin/rewards", { ...form, amount: Number(form.amount), paid: false });
      toast.success("Recompensa registrada");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Erro ao registrar");
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-reward-dialog">
        <DialogHeader><DialogTitle>Registrar recompensa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-muted-foreground">Nome do participante (opcional)
            <input className={`${inputCls} mt-1`} value={form.participant_name} onChange={set("participant_name")} data-testid="new-reward-name" />
          </label>
          <label className="block text-xs font-semibold text-muted-foreground">Perfil do Instagram (opcional)
            <input className={`${inputCls} mt-1`} value={form.instagram} onChange={set("instagram")} placeholder="@perfil" data-testid="new-reward-instagram" />
          </label>
          <label className="block text-xs font-semibold text-muted-foreground">Valor (R$)
            <input type="number" min={0} step="0.01" className={`${inputCls} mt-1`} value={form.amount} onChange={set("amount")} data-testid="new-reward-amount" />
          </label>
        </div>
        <DialogFooter>
          <button type="button" onClick={save} disabled={saving} data-testid="new-reward-save"
            className="bg-primary text-primary-foreground font-semibold rounded-full px-6 py-2.5 text-sm disabled:opacity-60">
            {saving ? "Salvando..." : "Registrar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RewardRow = ({ r, onToggle, onDelete }) => {
  const handle = instaHandle(r.instagram);
  return (
    <div className="bg-white rounded-2xl border border-border p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`reward-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900">{r.participant_name || "Participante não informado"}</p>
        {handle ? (
          <a href={instaUrl(r.instagram)} target="_blank" rel="noreferrer" className="text-sm text-primary inline-flex items-center gap-1"><Instagram className="w-3.5 h-3.5" />@{handle}</a>
        ) : <p className="text-sm text-muted-foreground">Instagram não informado</p>}
        <p className="text-xs text-muted-foreground mt-1">Gerada em {fmtDate(r.created_at)}{r.paid && r.paid_at ? ` · Paga em ${fmtDate(r.paid_at)}` : ""}</p>
      </div>
      <p className="text-lg font-bold font-heading text-slate-900" data-testid={`reward-amount-${r.id}`}>{formatBRL(r.amount)}</p>
      <span data-testid={`reward-status-${r.id}`} className={`text-xs font-bold rounded-full px-3 py-1 ${r.paid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
        {r.paid ? "Pago" : "Pendente"}
      </span>
      <div className="flex items-center gap-2">
        {r.paid ? (
          <button type="button" onClick={() => onToggle(r, false)} data-testid={`reward-undo-${r.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground border border-border rounded-full px-3 py-2 hover:bg-muted">
            <Undo2 className="w-3.5 h-3.5" /> Desfazer
          </button>
        ) : (
          <button type="button" onClick={() => onToggle(r, true)} data-testid={`reward-pay-${r.id}`} className="inline-flex items-center gap-1 text-xs font-bold bg-green-600 text-white rounded-full px-3 py-2 hover:bg-green-700">
            <CheckCircle2 className="w-4 h-4" /> PAGAMENTO REALIZADO
          </button>
        )}
        <button type="button" onClick={() => onDelete(r)} data-testid={`reward-delete-${r.id}`} aria-label="Excluir" className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default function RewardsManager() {
  const { settings } = useSite();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/rewards").then((r) => setItems(r.data)).catch(() => toast.error("Erro ao carregar recompensas"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => [...items].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")), [items]);
  const pending = sorted.filter((r) => !r.paid);
  const paid = sorted.filter((r) => r.paid);
  const sum = (list) => formatBRL(list.reduce((s, r) => s + (Number(r.amount) || 0), 0));
  const visible = filter === "pending" ? pending : filter === "paid" ? paid : sorted;

  const toggle = async (r, value) => {
    try {
      await api.put(`/admin/rewards/${r.id}`, { paid: value, paid_at: value ? new Date().toISOString() : "" });
      toast.success(value ? "Marcado como pago" : "Pagamento desfeito");
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };
  const remove = async (r) => {
    if (!window.confirm("Excluir esta recompensa?")) return;
    await api.delete(`/admin/rewards/${r.id}`).catch(() => toast.error("Erro ao excluir"));
    load();
  };

  return (
    <div className="space-y-5" data-testid="rewards-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center"><Gift className="w-5 h-5 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-bold font-heading text-slate-900">Recompensas</h1>
            <p className="text-sm text-muted-foreground">Você confere no Instagram, registra aqui e marca quando pagar.</p>
          </div>
        </div>
        <button type="button" onClick={() => setDialog(true)} data-testid="reward-new-btn" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-5 py-2.5 text-sm">
          <Plus className="w-4 h-4" /> Registrar recompensa
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Total geradas" value={sorted.length} sub={sum(sorted)} testId="reward-stat-total" />
        <Stat label="Pendentes" value={pending.length} sub={sum(pending)} testId="reward-stat-pending" />
        <Stat label="Pagas" value={paid.length} sub={sum(paid)} testId="reward-stat-paid" />
      </div>

      <div className="flex gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`reward-filter-${k}`}
            className={`text-sm font-semibold rounded-full px-4 py-1.5 border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-border text-slate-700"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="reward-list">
        {visible.length ? visible.map((r) => <RewardRow key={r.id} r={r} onToggle={toggle} onDelete={remove} />)
          : <p className="text-sm text-muted-foreground text-center py-10 bg-white rounded-2xl border border-border" data-testid="reward-empty">Nenhuma recompensa aqui.</p>}
      </div>

      <RewardProgramForm />

      <NewRewardDialog open={dialog} onOpenChange={setDialog} defaultAmount={settings?.reward_program?.amount || ""} onSaved={load} />
    </div>
  );
}
